/*** IMPORTS FROM imports-loader ***/
var define = false;

"use strict";

var R = require("ramda");
var util = require("./util");
var stringUtil = require("../util/string");
var numberUtil = require("../util/number");

var defaultLabels = {
    avg: "Avg ",
    max: "Max ",
    min: "Min ",
    sum: ""
};
var encodeCharRegex = new RegExp("&|\"|'|<|>", "g");
var titleCaseRegex = new RegExp("\\w\\S*", "g");


/**
 * HTML-encodes the specified string.
 *
 * Legacy implementation:
 * https://github.com/mleibman/SlickGrid/blob/master/slick.grid.js#L1362
 *
 * @param value
 *     String to be encoded.
 */
function encodeHtml(value) {
    return (value || "")
        .replace(encodeCharRegex, function (value) {
            switch (value) {
            case "&":
                return "&amp;";
            case "\"":
                return "&quot;";
            case "'":
                return "&#39;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            default:
                return value;
            }
        });
}


/**
 * Converts the specified string to title case.
 *
 * Example. "sammy tammi" => "Sammy Tammi"
 *
 * @param value
 *     String to be title cased.
 */
function toTitleCase(value) {
    return value.replace(
        titleCaseRegex,
        function (value) {
            return value.charAt(0)
                .toUpperCase()
                .concat(value.substr(1).toLowerCase());
        }
    );
}


/**
 * Parses the number, applies numeric formatting and adds bolding.
 *
 * Returns empty string `""` if the value is not a number.
 *
 * @param field
 *     Field to be used in the aggregate.
 */
function createAggregateFormatter(field) { // jshint validthis:true
    var getValue = util.propOrDefault(field);
    var labels = R.merge(defaultLabels, this.formatterLabels || {});
    var numberFormat = util.stringOrDefault("formatSpecifier", "N")(this);
    var mapTotals = R.compose(R.join(" "), R.map(function (pair) {
        var value = numberUtil.parseNumber(getValue(pair[1]));
        if (numberUtil.isNumber(value)) {
            var label = util.propOrDefault(pair[0], "")(labels);
            return label.concat("<b>", numberUtil.formatNumber(value, numberFormat), "</b>");
        }
        return "";
    }), R.toPairs);
    return function aggregateFormatter(totals) {
        return mapTotals(totals);
    };
}


/**
 * Displays a truthy icon or falsy icon based on the
 * value of the field.
 *
 * Adds an icon `formatterTrueIconClass` if the
 * value is truthy or `formatterFalseIconClass` if
 * the value is falsy.
 *
 * @param field
 *     Field to check for truthy/falsy.
 */
function createBoolIconFormatter(field) { // jshint validthis:true
    var getValue = util.propOrDefault(field, false);
    var truthyIcon = "<i class='".concat(util.stringOrDefault("formatterTrueIconClass", "")(this), "'></i>");
    var falsyIcon = "<i class='".concat(util.stringOrDefault("formatterFalseIconClass", "")(this), "'></i>");
    return function iconFormatter(item) {
        return (getValue(item) ? truthyIcon : falsyIcon);
    };
}


/**
 * Formats a slick.grid group title.
 *
 * Example. `Aberfeldy (241 items)`
 *
 * @param customFormatter
 *     Custom formatter applied to the group title.
 */
function createGroupFormatter(customFormatter) { // jshint validthis:true
    var prefix = util.stringOrDefault("title", "")(this);
    return function groupFormatter(item) {
        return (prefix ? (prefix.concat(": ")) : prefix).concat(
            customFormatter(item),
            " (",
            item.count,
            (item.count === 1 ? " item)" : " items)")
        );
    };
}


/**
 * Prepends an icon to a column using a string formatter for the field.
 *
 * The icon css class may be specified directly in the "formatterIconClass"
 * property on the column definition.
 *
 * The icon css class may also be derived from another field, specified in
 * the "formatterIconField" property on the column definition (defaults to "icon").
 *
 * @param field
 *     Field to append after the icon.
 */
function createIconFormatter(field) { // jshint validthis:true
    var getValue = R.compose(encodeHtml, util.stringOrDefault(field, ""));
    var iconClass = util.stringOrDefault("formatterIconClass", "")(this);
    var getIcon = (iconClass
        ? R.always("")
        : util.stringOrDefault(util.stringOrDefault("formatterIconField", "icon")(this), ""));
    return function iconFormatter(item) {
        var icon = iconClass || getIcon(item);
        return icon
            ? "<i class='".concat(icon, "'></i>", getValue(item))
            : getValue(item);
    };
}


/**
 * Creates a formatter method for date entries.
 *
 * Takes date objects or strings formatted like `yyyy-MM-dd`
 * and formats using `MMM-dd-yyyy` or the format specified in
 * "formatSpecifier" on the column.
 *
 * @param field
 *     Field to format as a date.
 */
function createDateFormatter(field) { // jshint validthis:true
    var getValue = R.pipe(
        // get value for 'field'
        util.propOrDefault(field, ""),
        util.asDate
    );
    var dateFormat = util.stringOrDefault("formatSpecifier", "MMM-dd-yyyy")(this);
    return function dateFormatter(item) {
        var dateValue = getValue(item);
        return (dateValue !== null ? dateValue.toString(dateFormat) : "");
    };
}


/**
 * Creates a formatter method for numeric entries.
 *
 * Takes number objects or strings and formats
 * using the default number format or the format specified in
 * "formatSpecifier" on the column.
 *
 * @param field
 *     Field to format as a number.
 */
function createNumberFormatter(field) { // jshint validthis:true
    var getValue = util.propOrDefault(field, null);
    var numberFormat = util.stringOrDefault("formatSpecifier", "N")(this);

    return function numberFormatter(item) {
        var value = getValue(item);

        if (numberUtil.isNumber(value)) {
            return numberUtil.formatNumber(value, numberFormat);
        }

        value = numberUtil.parseNumber(value);
        return numberUtil.formatNumber(value, numberFormat);
    };
}


/**
 * Creates a formatter method for validated columns.
 *
 * Looks for an error dictionary { [field:string]: message } in the
 * "errorSpecifier" column property, defaulting to "_errors".
 *
 * Error message override may be specified on the column definition
 * in the "errorMessage" property.
 *
 * @param field
 *     Field to format.
 * @param defaultColumnErrorMessage
 *     If specified, will be used as the default value for the column's
 *     "errorMessage" property, overriding the message in the "_errors"
 *     dictionary (may still be overriden by column "errorMessage" value).
 */
function createValidationFormatter(field, defaultColumnErrorMessage) { // jshint validthis:true
    var errorSpecifierField = util.stringOrDefault("errorSpecifier", "_errors")(this);
    var columnErrorMessage = util.stringOrDefault("errorMessage", defaultColumnErrorMessage)(this);
    var getValue = util.stringOrDefault(field, null);
    var getErrors = util.propOrDefault(errorSpecifierField, {});
    return function validationFormatter(item) {
        var errors = getErrors(item);
        return (errors[field]
            ? "<span class='error'>" + encodeHtml(columnErrorMessage || errors[field]) + "</span>"
            : encodeHtml(getValue(item)));
    };
}


/**
 * Default implementation of the formatter factory.
 *
 * @param id
 *     Key-value pair consisting of the column id and format type
 *     separated by ':'.
 *
 *     Example. "startDate:date" - [key] = startDate, [value] = date
 *
 * @returns {function}
 *     Formatter function based off the format type from id parameter.
 */
function defaultFactory(id) { // jshint validthis:true
    if (stringUtil.isNullOrWhitespace(id)) { throw new Error("factory id must be non-empty string."); }
    var pair = id.split(":");
    var formatterType = (pair[1] || "").toLowerCase();
    var formatterField = pair[0];
    switch (formatterType) {
    case "aggregate":
        return createAggregateFormatter.call(this, formatterField);
    case "blank":
        return function () { return ""; };
    case "bool-icon":
        return createBoolIconFormatter.call(this, formatterField);
    case "date":
        return createDateFormatter.call(this, formatterField);
    case "default":
        return R.compose(encodeHtml, util.stringOrDefault(formatterField, ""));
    case "group":
        var formatter = this.formatter !== undefined
            ? defaultFactory.call(this, "value:".concat(this.formatter.split(":")[1]))
            : util.stringOrDefault("value", "Empty");
        return createGroupFormatter.call(this, formatter);
    case "group-title":
        return createGroupFormatter.call(this, R.compose(toTitleCase, util.stringOrDefault("value", "Empty")));
    case "icon":
        return createIconFormatter.call(this, formatterField);
    case "lowercase":
        return R.compose(encodeHtml, R.toLower, util.stringOrDefault(formatterField, ""));
    case "number":
        return createNumberFormatter.call(this, formatterField);
    case "required":
        return createValidationFormatter.call(this, formatterField, "Empty");
    case "validated":
        return createValidationFormatter.call(this, formatterField);
    default:
        throw new Error("`".concat(formatterType, "` formatter type is not supported."));
    }
}


/**
 * Creates a function that resolves formatters by forwarding
 * requests to the specified factory in the specified context.
 *
 * @param factory
 *     Factory is specified so that the returned function can
 *     forward formatter requests to it via closure.
 */
function init(factory) {
    return function formatterResolver(context, value) {
        var formatter;
        if (typeof value === "function") {
            formatter = value;
        } else {
            formatter = factory.call(context, value);
        }
        return R.bind(formatter, context);
    };
}


module.exports = {
    init: init,
    defaultFactory: init(defaultFactory)
};




//////////////////
// WEBPACK FOOTER
// ./~/pli/ko-grid/formatterFactory.js
// module id = 438
// module chunks = 49